import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  X,
  ExternalLink,
  Calendar,
  Users,
  FileText,
  Vote,
  TrendingUp,
  Clock,
  Flag,
  Info,
  Tag,
  ScrollText,
  CheckCircle,
  XCircle,
  RotateCcw,
  Skull,
  PenTool,
  Landmark,
  Scale,
  ClipboardList,
  Building,
  ArrowLeft,
  Bookmark,
  Star,
} from "lucide-react";
import { buildApiUrl } from "../config";
import { calculatePartisanScore } from "../scripts/bills/partisanScoring";
import {
  getPartisanDataFromScore,
  calculatePartisanBreakdowns,
} from "../scripts/bills/partisanScoreUtils";
import {
  formatDate,
  getDaysActive,
  getMostRecentActionDate,
  getStatusColor,
  getBillCardData,
  getBillCardBadge,
} from "../scripts/bills/billUtils";
import {
  getPolicyTagColor,
  getPolicyTagIcon,
} from "../scripts/bills/policyTagUtils";
import InfoTooltip from "../components/InfoTooltip";
import { timelineStyles } from "../styles/timelineStyles";
import BillTimeline from "../components/BillTimeline";
import { formatSponsorName } from "../utils/formatSponsorName";
import {
  resolveBillId,
  toggleSaveBill,
  getCurrentUser,
} from "../lib/savedBills";
import { toggleFavoriteBill } from "../lib/favoritedBills";
import { supabase } from "../lib/supabaseClient";
import CommitteeCard from "../components/CommitteeCard";
import VoteDetails from "../components/VoteDetails";
import SponsorSection from "../components/SponsorSection";
import { fetchEventsByBill } from "../services/calendarService";
import FlagInaccuracy from "../components/FlagInaccuracy";

const BillDetail = ({ isDarkMode, setIsDarkMode }) => {
  const { billNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllVotes, setShowAllVotes] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [billId, setBillId] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showCommittees, setShowCommittees] = useState(false);
  const [showDetailedSummary, setShowDetailedSummary] = useState(false);
  const [showFlagInaccuracy, setShowFlagInaccuracy] = useState(false);

  // Calendar Events State
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Filter for past events only
  const pastEvents = useMemo(() => {
    const now = new Date();
    return calendarEvents.filter((event) => new Date(event.start_at) <= now);
  }, [calendarEvents]);

  const [voteDetailsCache, setVoteDetailsCache] = useState(new Map());
  const [expandedVoteFilter, setExpandedVoteFilter] = useState(new Map());
  console.log("🔍 BillDetail component rendered with billNumber:", billNumber);

  // Handler for clicking vote blocks
  const handleVoteBlockClick = (rollCallId, filterType) => {
    setExpandedVoteFilter(prev => {
      const newMap = new Map(prev);
      // If clicking the same filter, toggle off; otherwise set new filter
      if (newMap.get(rollCallId) === filterType) {
        newMap.delete(rollCallId);
      } else {
        newMap.set(rollCallId, filterType);
      }
      return newMap;
    });
  };

  // Get the return URL from location state, fallback to /bills
  const returnUrl = location.state?.returnUrl || "/bills";

  const fetchBillDetails = useCallback(async () => {
    if (!billNumber) {
      console.log("🔍 No billNumber provided, skipping fetch");
      return;
    }

    console.log("🔍 Fetching bill details for:", billNumber);
    setLoading(true);
    setError(null);

    try {
      // Normalize bill number from URL - try both with and without spaces
      // URL has no spaces (e.g., "SB120"), but database has spaces (e.g., "SB 120")
      const billNumberWithSpace = billNumber.replace(/([a-z]+)(\d+)/i, "$1 $2");
      const billNumberWithoutSpace = billNumber.replace(/\s+/g, "");
      // Try the version with space first (database format), then without space
      const billNumbersToTry = [
        billNumberWithSpace,
        billNumberWithoutSpace,
        billNumber,
      ];

      // Try batch endpoint first for more reliable fetching
      // Send both formats to the API
      const numbersParam = billNumbersToTry
        .map((n) => encodeURIComponent(n))
        .join(",");
      let apiUrl = buildApiUrl(`/api/bills/batch?numbers=${numbersParam}`);
      let response = await fetch(apiUrl);
      let billData = null;

      if (response.ok) {
        const data = await response.json();
        console.log("🔍 Batch endpoint response for", billNumber, ":", data);
        if (data.success && data.bills) {
          // Batch endpoint returns bills as an object, not an array
          if (Array.isArray(data.bills)) {
            // Try to find bill with any of the normalized formats
            billData = data.bills.find(
              (bill) =>
                billNumbersToTry.includes(bill.bill_number) ||
                bill.bill_number.replace(/\s+/g, "") === billNumberWithoutSpace,
            );
          } else {
            // If bills is an object, it might be keyed by bill number
            // Try all variations
            billData =
              billNumbersToTry
                .map((num) => data.bills[num])
                .find((bill) => bill !== undefined) ||
              Object.values(data.bills).find(
                (bill) =>
                  billNumbersToTry.includes(bill.bill_number) ||
                  bill.bill_number.replace(/\s+/g, "") ===
                  billNumberWithoutSpace,
              );
          }
          console.log("🔍 Found bill data from batch:", billData);
          if (billData) {
            console.log(
              "🔍 Batch data has history:",
              !!billData.history,
              "length:",
              billData.history?.length,
            );
            console.log(
              "🔍 Batch data has actions:",
              !!billData.actions,
              "length:",
              billData.actions?.length,
            );
            console.log(
              "🔍 Batch data has votes:",
              !!billData.votes,
              "length:",
              billData.votes?.length,
            );
            console.log(
              "🔍 Batch data has sponsors:",
              !!billData.sponsors,
              "length:",
              billData.sponsors?.length,
            );
            console.log(
              "🔍 Batch data has bill_id:",
              !!billData.bill_id,
              "value:",
              billData.bill_id,
            );
            console.log(
              "🔍 Batch data has _id:",
              !!billData._id,
              "value:",
              billData._id,
            );
            console.log(
              "🔍 Batch data has legiscan_bill_id:",
              !!billData.legiscan_bill_id,
              "value:",
              billData.legiscan_bill_id,
            );
            console.log("🔍 All batch data keys:", Object.keys(billData));
          }
        }
      }

      // Check if batch endpoint returned incomplete data (missing history/actions for legislative timeline)
      let batchDataIncomplete = false;
      let originalBatchData = null;
      if (
        billData &&
        (!billData.history ||
          !billData.actions ||
          billData.history.length === 0)
      ) {
        console.log(
          "🔄 Batch endpoint returned incomplete data (missing history/actions), trying alternative approaches for:",
          billNumber,
        );
        batchDataIncomplete = true;
        originalBatchData = billData; // Store the original batch data

        // Try to get complete data by using the id from batch data
        // Priority: id (UUID) -> legiscan_bill_id -> bill_id (legacy)
        if (billData.id) {
          console.log(
            "🔄 Trying to fetch complete data using id (UUID):",
            billData.id,
          );
          try {
            const completeResponse = await fetch(
              buildApiUrl(`/api/bills/${billData.id}`),
            );
            if (completeResponse.ok) {
              const completeData = await completeResponse.json();
              console.log("🔍 Complete data response:", completeData);
              if (
                completeData &&
                (completeData.history || completeData.actions)
              ) {
                console.log("✅ Found complete data using id");
                billData = completeData;
                batchDataIncomplete = false; // We found complete data
              } else {
                console.log(
                  "⚠️ Complete data response missing history/actions",
                );
              }
            } else {
              console.log(
                "❌ Complete data request failed with status:",
                completeResponse.status,
              );
            }
          } catch (err) {
            console.log("❌ Failed to fetch complete data using id:", err);
          }
        } else if (billData.legiscan_bill_id) {
          console.log(
            "🔄 No id available, trying to fetch complete data using legiscan_bill_id:",
            billData.legiscan_bill_id,
          );
          try {
            // Try using legiscan_bill_id to fetch complete data
            const completeResponse = await fetch(
              buildApiUrl(`/api/bills/${billData.legiscan_bill_id}`),
            );
            if (completeResponse.ok) {
              const completeData = await completeResponse.json();
              console.log(
                "🔍 Complete data response using legiscan_bill_id:",
                completeData,
              );
              if (
                completeData &&
                (completeData.history || completeData.actions)
              ) {
                console.log("✅ Found complete data using legiscan_bill_id");
                billData = completeData;
                batchDataIncomplete = false; // We found complete data
              } else {
                console.log(
                  "⚠️ Complete data response missing history/actions",
                );
              }
            } else {
              console.log(
                "❌ Complete data request failed with status:",
                completeResponse.status,
              );
            }
          } catch (err) {
            console.log(
              "❌ Failed to fetch complete data using legiscan_bill_id:",
              err,
            );
          }
        } else if (billData.bill_id) {
          console.log(
            "🔄 No id or legiscan_bill_id available, trying legacy bill_id:",
            billData.bill_id,
          );
          try {
            // Try using legacy bill_id to fetch complete data
            const completeResponse = await fetch(
              buildApiUrl(`/api/bills/${billData.bill_id}`),
            );
            if (completeResponse.ok) {
              const completeData = await completeResponse.json();
              console.log(
                "🔍 Complete data response using bill_id:",
                completeData,
              );
              if (
                completeData &&
                (completeData.history || completeData.actions)
              ) {
                console.log("✅ Found complete data using bill_id");
                billData = completeData;
                batchDataIncomplete = false; // We found complete data
              } else {
                console.log(
                  "⚠️ Complete data response missing history/actions",
                );
              }
            } else {
              console.log(
                "❌ Complete data request failed with status:",
                completeResponse.status,
              );
            }
          } catch (err) {
            console.log("❌ Failed to fetch complete data using bill_id:", err);
          }
        } else {
          console.log(
            "⚠️ No id, legiscan_bill_id, or bill_id available in batch data",
          );

          // Try to find the id by searching for the exact bill number with a different approach
          // Try searching with normalized bill number (with space)
          const billNumberWithSpace = billNumber.replace(
            /([a-z]+)(\d+)/i,
            "$1 $2",
          );
          console.log(
            "🔄 Trying to find id by searching for exact bill number:",
            billNumberWithSpace,
          );
          try {
            // Try searching with exact match and higher limit to find the right bill
            const searchResponse = await fetch(
              buildApiUrl(
                `/api/bills?search=${encodeURIComponent(billNumberWithSpace)}&limit=10`,
              ),
            );
            if (searchResponse.ok) {
              const searchData = await searchResponse.json();
              console.log("🔍 Extended search results:", searchData);
              if (searchData.bills && searchData.bills.length > 0) {
                console.log(
                  "🔍 Extended search bills array:",
                  searchData.bills,
                );
                console.log(
                  "🔍 Looking for exact bill number:",
                  billNumberWithSpace,
                );
                console.log(
                  "🔍 Available bill numbers in extended search:",
                  searchData.bills.map((b) => b.bill_number),
                );

                // Look for exact bill number match - try both with and without spaces
                const exactMatch = searchData.bills.find(
                  (bill) =>
                    bill.bill_number === billNumberWithSpace ||
                    bill.bill_number.replace(/\s+/g, "") ===
                    billNumber.replace(/\s+/g, ""),
                );
                if (exactMatch) {
                  // Try id (UUID) first, then legiscan_bill_id, then bill_id (legacy)
                  const billId =
                    exactMatch.id ||
                    exactMatch.legiscan_bill_id ||
                    exactMatch.bill_id;
                  if (billId) {
                    console.log("✅ Found exact match with id:", billId);
                    // Now try to fetch complete data using this id
                    const completeResponse = await fetch(
                      buildApiUrl(`/api/bills/${billId}`),
                    );
                    if (completeResponse.ok) {
                      const completeData = await completeResponse.json();
                      console.log(
                        "🔍 Complete data from exact match:",
                        completeData,
                      );
                      if (
                        completeData &&
                        (completeData.history || completeData.actions)
                      ) {
                        console.log(
                          "✅ Found complete data using exact match id",
                        );
                        billData = completeData;
                        batchDataIncomplete = false; // We found complete data
                      }
                    }
                  } else {
                    console.log("⚠️ Exact match found but no id available");
                  }
                } else {
                  console.log(
                    "⚠️ No exact match found in extended search results",
                  );
                  console.log(
                    "🔍 Extended search returned bills:",
                    searchData.bills.map((b) => ({
                      bill_number: b.bill_number,
                      id: b.id,
                      legiscan_bill_id: b.legiscan_bill_id,
                    })),
                  );
                }
              }
            }
          } catch (err) {
            console.log("❌ Failed to find bill_id via extended search:", err);
          }
        }

        // If we still don't have complete data, try search endpoint
        if (batchDataIncomplete) {
          billData = null; // Reset to try search endpoint
        }
      }

      // Fallback to search endpoint if batch fails or doesn't return the bill
      if (!billData) {
        // Try search with normalized bill number (with space)
        const billNumberWithSpace = billNumber.replace(
          /([a-z]+)(\d+)/i,
          "$1 $2",
        );
        console.log(
          "🔄 Batch endpoint failed, trying search endpoint for:",
          billNumberWithSpace,
        );
        apiUrl = buildApiUrl(
          `/api/bills?search=${encodeURIComponent(billNumberWithSpace)}&limit=1`,
        );
        response = await fetch(apiUrl);

        if (!response.ok) {
          setError("Failed to load bill details");
          return;
        }

        const data = await response.json();
        console.log("🔍 Search endpoint response for", billNumber, ":", data);
        if (data.bills && data.bills.length > 0) {
          console.log("🔍 Search endpoint bills array:", data.bills);
          console.log("🔍 Looking for bill number:", billNumber);
          console.log(
            "🔍 Available bill numbers:",
            data.bills.map((b) => b.bill_number),
          );
          console.log("🔍 First bill in search results:", data.bills[0]);

          // Check if search endpoint returned wrong bill (common issue with high page numbers)
          const foundBill =
            data.bills.find((bill) => bill.bill_number === billNumber) ||
            data.bills.find(
              (bill) =>
                bill.bill_number?.toLowerCase() === billNumber.toLowerCase(),
            );

          if (!foundBill && data.bills.length > 0) {
            console.log(
              "⚠️ Search endpoint returned wrong bill(s), falling back to batch data for:",
              billNumber,
            );
            // Don't set billData, let it fall through to batch data fallback
          } else {
            billData = foundBill;
            console.log("🔍 Found bill data:", billData);
          }
        }
      }

      // If all endpoints failed but we have incomplete batch data, use it as a last resort
      if (!billData && batchDataIncomplete && originalBatchData) {
        console.log(
          "🔄 All search endpoints failed, using stored incomplete batch data as fallback for:",
          billNumber,
        );
        billData = originalBatchData;
        console.log(
          "🔄 Using stored incomplete batch data as fallback:",
          billData,
        );
      }

      if (!billData) {
        setError("Bill not found");
        return;
      }

      // Helper to get latest action date excluding "Effective Date" (frontend-only fix)
      const getMostRecentActionDateExcludingEffective = (bill) => {
        if (!bill.history || bill.history.length === 0) {
          return getMostRecentActionDate(bill);
        }
        // Find first action that is NOT "Effective Date"
        const nonEffectiveAction = bill.history.find(action => {
          const actionLower = (action.action || '').toLowerCase();
          const code = action.code || '';
          return code !== 'EFF' && !actionLower.includes('effective date');
        });
        return nonEffectiveAction?.date || getMostRecentActionDate(bill);
      };

      const billWithStatus = {
        ...billData,
        mostRecentDate: getMostRecentActionDateExcludingEffective(billData),
      };
      setBill(billWithStatus);
    } catch (err) {
      console.error("Error fetching bill details:", err);
      setError("Failed to load bill details");
    } finally {
      setLoading(false);
    }
  }, [billNumber]);

  useEffect(() => {
    fetchBillDetails();
  }, [fetchBillDetails]);

  // Resolve bill id and saved state
  useEffect(() => {
    (async () => {
      try {
        if (!bill) return;
        const id = bill.id || (await resolveBillId(bill));
        setBillId(id);
        const user = await getCurrentUser();
        if (!user || !id) {
          setIsSaved(false);
          setIsFavorited(false);
          return;
        }
        const { data, error } = await supabase
          .from("saved_bills")
          .select("bill_id")
          .eq("user_id", user.id)
          .eq("bill_id", id)
          .maybeSingle();
        if (!error && data?.bill_id) setIsSaved(true);
        else setIsSaved(false);

        const { data: fav, error: favErr } = await supabase
          .from("favorited_bills")
          .select("bill_id")
          .eq("user_id", user.id)
          .eq("bill_id", id)
          .maybeSingle();
        if (!favErr && fav?.bill_id) setIsFavorited(true);
        else setIsFavorited(false);
      } catch {
        setIsSaved(false);
        setIsFavorited(false);
      }
    })();
  }, [bill]);

  // Fetch calendar events when bill is loaded
  useEffect(() => {
    if (bill && (bill.id || bill.bill_id)) {
      const fetchEvents = async () => {
        setLoadingEvents(true);
        try {
          // Try both UUID and legacy ID if available
          const idToUse = bill.id || bill.bill_id;
          const data = await fetchEventsByBill(idToUse);
          if (data && data.events) {
            setCalendarEvents(data.events);
          }
        } catch (err) {
          console.error("Failed to fetch bill events:", err);
        } finally {
          setLoadingEvents(false);
        }
      };
      fetchEvents();
    }
  }, [bill]);

  const handleToggleSave = useCallback(async () => {
    try {
      const id = billId || (bill && (bill.id || (await resolveBillId(bill))));
      if (!id) {
        alert("Unable to save this bill (missing id).");
        return;
      }
      // Require login
      const user = await getCurrentUser();
      if (!user) {
        const returnTo = encodeURIComponent(location.pathname);
        navigate(`/signin?return=${returnTo}`);
        return;
      }
      const next = await toggleSaveBill(id, !isSaved);
      setIsSaved(next);
    } catch (e) {
      alert(e?.message || "Failed to update saved state.");
    }
  }, [bill, billId, isSaved, navigate, location.pathname]);

  const handleToggleFavorite = useCallback(async () => {
    try {
      const id = billId || (bill && (bill.id || (await resolveBillId(bill))));
      if (!id) {
        alert("Unable to favorite this bill (missing id).");
        return;
      }
      // Require login
      const user = await getCurrentUser();
      if (!user) {
        const returnTo = encodeURIComponent(location.pathname);
        navigate(`/signin?return=${returnTo}`);
        return;
      }
      const next = await toggleFavoriteBill(id, !isFavorited);
      setIsFavorited(next);
    } catch (e) {
      alert(e?.message || "Failed to update favorite state.");
    }
  }, [bill, billId, isFavorited, navigate, location.pathname]);

  const handleFlagInaccuracyClick = useCallback(async () => {
    // Require login
    const user = await getCurrentUser();
    if (!user) {
      const returnTo = encodeURIComponent(location.pathname);
      navigate(`/signin?return=${returnTo}`);
      return;
    }
    setShowFlagInaccuracy(true);
  }, [navigate, location.pathname]);

  const handleClose = useCallback(() => {
    // Capture live background scroll position at close time so we return
    // to where the list currently is, not where it was before opening.
    const currentScrollPosition = window.scrollY;
    sessionStorage.setItem(
      "billsPageScrollPosition",
      currentScrollPosition.toString(),
    );
    localStorage.setItem(
      "billsPageScrollPosition",
      currentScrollPosition.toString(),
    );

    console.log(
      "💾 [PRODUCTION DEBUG] Closing bill detail, navigating to:",
      returnUrl,
    );

    // Use replace: true when coming from calendar to prevent history stack buildup
    // This way Calendar -> Bill -> Calendar doesn't add entries, keeping history clean
    const fromCalendar = location.state?.fromCalendar;
    navigate(returnUrl, {
      replace: fromCalendar, // Replace history entry if we came from calendar
      state: { preserveScroll: true },
    });
  }, [navigate, returnUrl, location.state?.fromCalendar]);

  const handleToggleVotes = () => {
    setShowAllVotes(!showAllVotes);
  };

  if (loading) {
    console.log("🔍 BillDetail rendering loading state");
    return (
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50 backdrop-blur-sm`}
      >
        <div
          className={`${isDarkMode ? "bg-gray-800 dark" : "bg-white"} rounded-2xl w-full max-w-[90vw] md:max-w-4xl max-h-[85vh] md:max-h-[90vh] shadow-2xl flex flex-col mx-auto`}
        >
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p
                className={`text-lg ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Loading bill details...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50 backdrop-blur-sm`}
      >
        <div
          className={`${isDarkMode ? "bg-gray-800 dark" : "bg-white"} rounded-2xl w-full max-w-[90vw] md:max-w-4xl max-h-[85vh] md:max-h-[90vh] shadow-2xl flex flex-col mx-auto`}
        >
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h2
                className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2`}
              >
                Error
              </h2>
              <p
                className={`${isDarkMode ? "text-gray-300" : "text-gray-600"} mb-4`}
              >
                {error || "Bill not found"}
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  console.log("🔍 BillDetail rendering main modal for bill:", bill);

  // Use new Card Status System for Header Display
  // This ensures consistency with the Bill Card on the listing page
  // Use new Card Status System for Header Display
  // This ensures consistency with the Bill Card on the listing page
  const cardData = getBillCardData(bill);
  const cardBadge = getBillCardBadge(bill, cardData.status);

  return (
    <>
      <style>{timelineStyles}</style>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50 backdrop-blur-sm transition-opacity duration-300 opacity-100`}
      >
        <div
          className={`${isDarkMode ? "bg-gray-800 dark" : "bg-white"} rounded-2xl w-full max-w-[90vw] md:max-w-4xl max-h-[85vh] md:max-h-[90vh] shadow-2xl transform flex flex-col modal-scrollbar mx-auto`}
          style={{
            animation: "modal-in 0.25s cubic-bezier(0.4,0,0.2,1) both",
          }}
        >
          {/* Modal Header */}
          <div
            className={`sticky top-0 z-20 ${isDarkMode ? "bg-gray-800" : "bg-white"} border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} p-4 md:p-6 rounded-t-2xl shadow-md backdrop-blur-sm`}
          >
            <div className="flex items-start justify-between gap-4 relative">
              {/* Left: Bill info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2 flex-wrap">
                  <h2
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {bill.bill_number}
                  </h2>

                  {/* Primary Status Label (Colored Pill) */}
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold border border-transparent ${getStatusColor(cardData.status, isDarkMode)}`}
                  >
                    {cardData.label}
                  </span>

                  {/* Secondary Badge (Subtle Pill) - Only if exists */}
                  {cardBadge && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cardBadge.color === "red"
                        ? isDarkMode
                          ? "bg-red-900/30 text-red-300 border-red-800"
                          : "bg-red-50 text-red-700 border-red-100"
                        : cardBadge.color === "amber"
                          ? isDarkMode
                            ? "bg-amber-900/30 text-amber-300 border-amber-800"
                            : "bg-amber-50 text-amber-700 border-amber-100"
                          : isDarkMode
                            ? "bg-gray-800 text-gray-400 border-gray-700"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                    >
                      {cardBadge.label}
                    </span>
                  )}
                </div>
                <h3
                  className={`text-lg font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"} mb-2`}
                >
                  {bill.display_title || bill.title}
                </h3>
                {/* Tags row with Save button aligned to the right */}
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-2 relative">
                  {/* Tags container with hard cut-off on mobile - stops before flag button */}
                  <div className="flex-1 md:flex-1 md:min-w-0 overflow-hidden md:overflow-visible mr-36 md:mr-2">
                    {/* Tags - horizontally scrollable on mobile, hard cut-off before buttons */}
                    <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 -mx-2 px-2 md:mx-0 md:px-0 md:flex-wrap custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {bill.policy_tags && bill.policy_tags.length > 0 && (
                        <>
                          {bill.policy_tags.map((tag, idx) => {
                            const IconComponent = getPolicyTagIcon(tag);
                            return (
                              <span
                                key={idx}
                                className={`inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-semibold flex-shrink-0 ${getPolicyTagColor(tag, isDarkMode)} shadow-sm border-2 border-transparent`}
                              >
                                <IconComponent className="w-3 h-3 mr-1 sm:mr-1.5" />
                                {tag}
                              </span>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                  {/* Action buttons - bottom right on mobile, inline on desktop - never shrink */}
                  <div className="flex items-center gap-1.5 md:gap-2 md:static fixed bottom-4 right-4 z-30 md:z-auto flex-shrink-0">
                    <button
                      onClick={handleFlagInaccuracyClick}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-100"} border ${isDarkMode ? "border-gray-700" : "border-gray-200"} shadow-sm`}
                      title="Flag inaccuracy"
                      aria-label="Flag inaccuracy"
                    >
                      <Flag
                        className={`w-5 h-5 ${isDarkMode ? "text-red-300" : "text-red-600"}`}
                      />
                    </button>
                    <button
                      onClick={handleToggleFavorite}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-100"} border ${isDarkMode ? "border-gray-700" : "border-gray-200"} shadow-sm`}
                      title={isFavorited ? "Unfavorite bill" : "Favorite bill"}
                      aria-label={
                        isFavorited ? "Unfavorite bill" : "Favorite bill"
                      }
                    >
                      {isFavorited ? (
                        <Star
                          className={`w-5 h-5 ${isDarkMode ? "text-yellow-300" : "text-yellow-500"}`}
                          fill={isDarkMode ? "#fde68a" : "#f59e0b"}
                        />
                      ) : (
                        <Star
                          className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        />
                      )}
                    </button>
                    <button
                      onClick={handleToggleSave}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-100"} border ${isDarkMode ? "border-gray-700" : "border-gray-200"} shadow-sm`}
                      title={isSaved ? "Unsave bill" : "Save bill"}
                      aria-label={isSaved ? "Unsave bill" : "Save bill"}
                    >
                      {isSaved ? (
                        <svg
                          viewBox="0 0 24 24"
                          className={`w-5 h-5 ${isDarkMode ? "text-blue-300" : "text-blue-600"}`}
                          fill="currentColor"
                        >
                          <path d="M6 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16l-6-3.5L6 20V4z" />
                        </svg>
                      ) : (
                        <Bookmark
                          className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              {/* Close Button */}
              <button
                onClick={handleClose}
                className={`${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"} transition-colors p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-full absolute top-2 right-2 md:-top-2 md:-right-5`}
                style={{ zIndex: 10 }}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Content - Scrollable */}
          <div className="flex-1 overflow-y-auto modal-scrollbar">
            <div className="p-4 md:p-6 pt-2 md:pt-4 pb-20 md:pb-6 space-y-4 md:space-y-6">
              {/* Bill Summary Section - Redesigned */}
              <section
                className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3
                      className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {bill.ai_summary ? "Summary" : "Description"}
                    </h3>
                  </div>
                  {bill.ai_summary && bill.detailed_summary && (
                    <button
                      onClick={() => {
                        setShowDetailedSummary(!showDetailedSummary);
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${isDarkMode
                        ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                        : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                        }`}
                    >
                      {showDetailedSummary ? "Show Simple" : "Show Detailed"}
                    </button>
                  )}
                </div>

                <div
                  className={`prose max-w-none ${isDarkMode ? "prose-invert" : ""}`}
                >
                  <style>{`
                  #modal-summary-${bill.id || bill._id || 'default'} ul {
                    margin: 0;
                    padding: 0;
                    list-style: none;
                  }
                  #modal-summary-${bill.id || bill._id || 'default'} li {
                    margin: 0.5rem 0;
                    padding: 0;
                    line-height: 1.5;
                    display: flex;
                    align-items: flex-start;
                  }
                  #modal-summary-${bill.id || bill._id || 'default'} li::before {
                    content: "•";
                    margin-right: 0.5rem;
                    flex-shrink: 0;
                    width: 1rem;
                  }
                  #modal-summary-${bill.id || bill._id || 'default'} li:first-child {
                    margin-top: 0;
                  }
                  #modal-summary-${bill.id || bill._id || 'default'} li:last-child {
                    margin-bottom: 0;
                  }
                `}</style>
                  <div
                    id={`modal-summary-${bill.id || bill._id || 'default'}`}
                    className={`${isDarkMode ? "text-gray-200" : "text-gray-700"} leading-relaxed text-base`}
                    dangerouslySetInnerHTML={{
                      __html: showDetailedSummary && bill.detailed_summary
                        ? bill.detailed_summary
                        : bill.ai_summary || bill.description || "No description available."
                    }}
                  />
                </div>

                {bill.ai_summary && bill.description && (
                  <details className="mt-4">
                    <summary
                      className={`text-sm cursor-pointer ${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-700"} font-medium`}
                    >
                      View original legislative text →
                    </summary>
                    <div
                      className={`mt-3 p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100/50"} border-l-4 ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                    >
                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} leading-relaxed`}
                      >
                        {bill.description}
                      </p>
                    </div>
                  </details>
                )}
              </section>

              {/* Previous Events / History */}
              {pastEvents.length > 0 && (
                <section
                  className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm`}
                >
                  {/* Header with Session Info */}
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <h3
                        className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"} inline-flex items-center gap-2`}
                      >
                        <Calendar className="w-5 h-5 text-blue-500" />
                        History
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-blue-900/40 text-blue-300" : "bg-blue-100 text-blue-700"}`}
                        >
                          {pastEvents.length}
                        </span>
                      </h3>
                      {bill.session_name && (
                        <p
                          className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {bill.session_name}
                        </p>
                      )}
                    </div>


                  </div>

                  {/* Event List */}
                  <div className="space-y-3">
                    {pastEvents.map((event, idx) => (
                      <Link
                        to={`/calendar?date=${new Date(event.start_at).toISOString().split("T")[0]}&eventId=${event.id}`}
                        key={idx}
                        className={`p-3 rounded-lg border ${isDarkMode ? "bg-gray-700/50 border-gray-600 hover:bg-gray-700" : "bg-gray-50 border-gray-200 hover:bg-gray-100"} flex items-start gap-3 transition-colors group`}
                      >
                        <div
                          className={`p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 shrink-0`}
                        >
                          <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p
                            className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {/* Strip bill number prefix from title (e.g., "HR1059: House Read" -> "House Read") */}
                            {event.display_title ||
                              event.extracted_text ||
                              event.description ||
                              (event.title?.includes(": ")
                                ? event.title.split(": ").slice(1).join(": ")
                                : event.title) ||
                              event.event_type?.replace(/_/g, " ") ||
                              "Scheduled Event"}
                          </p>
                          <div
                            className={`flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(event.start_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(event.start_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Committee Assignments Section */}
              {bill.committees && bill.committees.length > 0 && (
                <section
                  className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3
                        className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <InfoTooltip
                          content="Committees are groups of legislators appointed to review, amend, and vote on bills before they are presented to the full chamber for a vote."
                          isDarkMode={isDarkMode}
                          size="md"
                        >
                          Committees
                        </InfoTooltip>
                      </h3>
                    </div>

                  </div>

                  <div className="space-y-4">
                    {bill.committees.map((committee, idx) => (
                      <CommitteeCard
                        key={committee.id || idx}
                        committee={committee}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Timeline and Analysis Grid - Simplified */}
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Simplified Legislative Timeline with Visual Progress */}
                {bill.history && bill.history.length > 0 && (
                  <div
                    className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm flex flex-col`}
                  >
                    <h3
                      className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2 inline-flex items-center gap-2`}
                    >
                      Legislative Progress
                      <InfoTooltip
                        content="This timeline shows the 5 key milestones in the legislative process: Introduction, Committee Review, House Vote, Senate Vote, and Final Action."
                        title="Legislative Timeline"
                        size="sm"
                        position="right"
                        iconClassName="opacity-70"
                      >
                        <span></span>
                      </InfoTooltip>
                    </h3>

                    <div className="mt-2">
                      <BillTimeline bill={bill} isDarkMode={isDarkMode} />
                    </div>
                  </div>
                )}

                {/* Partisan Analysis - Enhanced */}
                {(() => {
                  // Use stored partisan score from database if available, otherwise calculate it
                  const basePartisanData =
                    bill.partisan_score !== undefined &&
                      bill.partisan_score !== null
                      ? getPartisanDataFromScore(bill.partisan_score)
                      : calculatePartisanScore(bill) || null;
                  if (!basePartisanData) return null;

                  // Calculate breakdowns from bill data (sponsors and votes)
                  const breakdowns = calculatePartisanBreakdowns(bill);

                  // Merge base data with breakdowns
                  const partisanData = {
                    ...basePartisanData,
                    ...breakdowns,
                  };

                  return (
                    <div
                      className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm flex flex-col`}
                    >
                      <div className="mb-4">
                        <h3
                          className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"} inline-flex items-center gap-2`}
                        >
                          <InfoTooltip
                            content="Partisan Analysis measures political support for bills based on political party"
                            isDarkMode={isDarkMode}
                            size="md"
                          >
                            Partisan Analysis
                          </InfoTooltip>
                        </h3>
                      </div>

                      <div className="space-y-4 flex-1">
                        {/* Overall Score Display */}
                        <div className="text-center">
                          <div
                            className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-1`}
                          >
                            <InfoTooltip
                              content={
                                <div className="text-center">
                                  <div>Score =</div>
                                  <div>0.5 × (R% − D% from sponsors)</div>
                                  <div>+</div>
                                  <div>0.5 × (R% − D% from final vote)</div>
                                </div>
                              }
                              isDarkMode={isDarkMode}
                              size="lg"
                            >
                              {partisanData.score}
                            </InfoTooltip>
                          </div>
                          <div
                            className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {partisanData.label}
                          </div>
                        </div>

                        {/* Enhanced Partisan Bar */}
                        <div
                          className="relative"
                          style={{
                            paddingLeft: "0.375rem",
                            paddingRight: "0.375rem",
                          }}
                        >
                          <div
                            className={`w-full h-6 relative overflow-hidden shadow-inner`}
                            style={{
                              background: isDarkMode
                                ? "linear-gradient(to right, #1e40af, #3b82f6, #6b7280, #ef4444, #dc2626)"
                                : "linear-gradient(to right, #1e3a8a, #2563eb, #9ca3af, #f87171, #b91c1c)",
                            }}
                          >
                            <div className="absolute left-1/2 top-0 h-full w-0.5 bg-white transform -translate-x-1/2"></div>
                          </div>
                          {/* Marker positioned to extend outside bar at extremes - uses padding to allow overflow */}
                          <div
                            className="absolute top-0 h-6 w-1.5 bg-black transform -translate-x-1/2 z-10"
                            style={{
                              left: `calc(${((partisanData.signedScore + 100) / 200) * 100}% ${partisanData.signedScore > 0 ? "- 0.75rem" : partisanData.signedScore < 0 ? "+ 0.75rem" : ""})`,
                              boxShadow: "0 0 8px rgba(0,0,0,0.5)",
                            }}
                          ></div>
                          <div className="flex justify-between text-xs mt-2 font-medium">
                            <span
                              className={`${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                            >
                              Democratic
                            </span>
                            <span
                              className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Neutral
                            </span>
                            <span
                              className={`${isDarkMode ? "text-red-400" : "text-red-600"}`}
                            >
                              Republican
                            </span>
                          </div>
                        </div>

                        {/* Compact Analysis Cards */}
                        <div className="space-y-3">
                          <div
                            className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/60" : "bg-white/80"} border ${isDarkMode ? "border-gray-600/50" : "border-gray-200/50"} backdrop-blur-sm`}
                          >
                            <h4
                              className={`text-sm font-bold mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"} flex items-center`}
                            >
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                              Sponsors
                            </h4>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div className="text-center">
                                <div
                                  className={`font-bold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                                >
                                  {partisanData.democratSponsors}
                                </div>
                                <div
                                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  Dem
                                </div>
                              </div>
                              <div className="text-center">
                                <div
                                  className={`font-bold ${isDarkMode ? "text-red-400" : "text-red-600"}`}
                                >
                                  {partisanData.republicanSponsors}
                                </div>
                                <div
                                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  Rep
                                </div>
                              </div>
                              <div className="text-center">
                                <div
                                  className={`font-bold flex items-center justify-center gap-1`}
                                >
                                  <span
                                    className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    {Math.abs(partisanData.sponsorScore)}
                                  </span>
                                  {partisanData.sponsorScore !== 0 && (
                                    <span
                                      className={`text-xs px-1 py-0.5 rounded ${partisanData.sponsorScore > 0
                                        ? isDarkMode
                                          ? "bg-red-900/50 text-red-300"
                                          : "bg-red-100 text-red-700"
                                        : isDarkMode
                                          ? "bg-blue-900/50 text-blue-300"
                                          : "bg-blue-100 text-blue-700"
                                        }`}
                                    >
                                      {partisanData.sponsorScore > 0
                                        ? "R"
                                        : "D"}
                                    </span>
                                  )}
                                </div>
                                <div
                                  className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  Score
                                </div>
                              </div>
                            </div>
                          </div>

                          {partisanData.hasVoteData && (
                            <div
                              className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/60" : "bg-white/80"} border ${isDarkMode ? "border-gray-600/50" : "border-gray-200/50"} backdrop-blur-sm`}
                            >
                              <h4
                                className={`text-sm font-bold mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"} flex items-center`}
                              >
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                                Voting
                              </h4>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center">
                                  <div
                                    className={`font-bold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                                  >
                                    {partisanData.demYeaPercent}%
                                  </div>
                                  <div
                                    className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    Dem
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div
                                    className={`font-bold ${isDarkMode ? "text-red-400" : "text-red-600"}`}
                                  >
                                    {partisanData.repYeaPercent}%
                                  </div>
                                  <div
                                    className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    Rep
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div
                                    className={`font-bold flex items-center justify-center gap-1`}
                                  >
                                    <span
                                      className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                    >
                                      {Math.abs(partisanData.voteScore)}
                                    </span>
                                    {partisanData.voteScore !== 0 && (
                                      <span
                                        className={`text-xs px-1 py-0.5 rounded ${partisanData.voteScore > 0
                                          ? isDarkMode
                                            ? "bg-red-900/50 text-red-300"
                                            : "bg-red-100 text-red-700"
                                          : isDarkMode
                                            ? "bg-blue-900/50 text-blue-300"
                                            : "bg-blue-100 text-blue-700"
                                          }`}
                                      >
                                        {partisanData.voteScore > 0 ? "R" : "D"}
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    Score
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </section>

              {/* Voting Records Section - Enhanced */}
              {bill.votes && bill.votes.length > 0 && (
                <section
                  className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center space-x-2">
                      <h3
                        className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Voting Records
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                      >
                        {bill.votes.length}
                      </span>
                    </div>
                    {bill.votes.length > 1 && (
                      <button
                        onClick={() => setShowAllVotes(!showAllVotes)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${isDarkMode
                          ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          }`}
                      >
                        {showAllVotes ? "Recent Only" : `View All`}
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {(showAllVotes
                      ? bill.votes.slice().reverse()
                      : bill.votes.slice().reverse().slice(0, 1)
                    ).map((vote, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg p-3 border ${isDarkMode ? "border-gray-600/30 bg-gray-800/50" : "border-gray-200/50 bg-white/80"} backdrop-blur-sm shadow-md hover:shadow-lg transition-all duration-300`}
                      >
                        {/* Vote Header - Compact */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${vote.passed
                                ? isDarkMode
                                  ? "bg-gradient-to-r from-green-600 to-green-500 text-white"
                                  : "bg-gradient-to-r from-green-500 to-green-600 text-white"
                                : isDarkMode
                                  ? "bg-gradient-to-r from-red-600 to-red-500 text-white"
                                  : "bg-gradient-to-r from-red-500 to-red-600 text-white"
                                }`}
                            >
                              {vote.passed ? (
                                <>
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  PASSED
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  FAILED
                                </>
                              )}
                            </div>

                            <div className="text-xs">
                              <span
                                className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                              >
                                {formatDate(vote.date)}
                              </span>
                              {vote.chamber_name && (
                                <span
                                  className={`ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  • {vote.chamber_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {vote.description && (
                          <div
                            className={`p-2 rounded ${isDarkMode ? "bg-gray-700/30" : "bg-gray-100/50"} border-l-2 ${vote.passed ? (isDarkMode ? "border-green-500" : "border-green-400") : isDarkMode ? "border-red-500" : "border-red-400"} mb-2`}
                          >
                            <p
                              className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"} leading-relaxed`}
                            >
                              {vote.description}
                            </p>
                          </div>
                        )}

                        {vote.motion && (
                          <div
                            className={`inline-flex items-center px-2 py-1 rounded text-xs ${isDarkMode ? "bg-blue-900/20 text-blue-300" : "bg-blue-100 text-blue-700"} font-medium mb-2`}
                          >
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Motion: {vote.motion}
                          </div>
                        )}

                        {/* Vote Statistics - Compact */}
                        <div className="mt-3">
                          <div className="grid grid-cols-4 gap-2 mb-3">
                            {/* Yea Votes */}
                            <div
                              onClick={() => handleVoteBlockClick(vote.roll_call_id, 'yea')}
                              className={`relative group p-2 rounded cursor-pointer ${isDarkMode ? "bg-green-900/20" : "bg-green-50"} border ${isDarkMode ? "border-green-700/30" : "border-green-200"} hover:border-green-500 transition-all duration-300`}
                            >
                              <div className="text-center">
                                <div
                                  className={`text-lg font-bold ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                                >
                                  {vote.yea}
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-green-300" : "text-green-700"} uppercase tracking-wide font-semibold`}
                                >
                                  Yea
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {vote.total > 0
                                    ? ((vote.yea / vote.total) * 100).toFixed(0)
                                    : 0}
                                  %
                                </div>
                              </div>

                              {/* Party Breakdown Tooltip for Yea */}
                              {(() => {
                                const matchingBreakdown =
                                  bill.vote_breakdowns && vote.roll_call_id
                                    ? bill.vote_breakdowns.find(
                                      (bd) =>
                                        bd.rollCallId === vote.roll_call_id,
                                    )
                                    : null;

                                if (!matchingBreakdown?.partyVoteCounts)
                                  return null;

                                const yeaBreakdown = {
                                  Democrat:
                                    matchingBreakdown.partyVoteCounts.Democrat
                                      ?.Yea || 0,
                                  Republican:
                                    matchingBreakdown.partyVoteCounts.Republican
                                      ?.Yea || 0,
                                  Other:
                                    matchingBreakdown.partyVoteCounts.Other
                                      ?.Yea || 0,
                                };

                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div
                                      className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}
                                    >
                                      <div className="text-xs font-semibold mb-1 text-center">
                                        Yea Votes by Party
                                      </div>
                                      <div className="space-y-1">
                                        {Object.entries(yeaBreakdown).map(
                                          ([party, count]) => (
                                            <div
                                              key={party}
                                              className="flex justify-between items-center text-xs"
                                            >
                                              <span
                                                className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${party === "Democrat"
                                                  ? "bg-blue-100 text-blue-800"
                                                  : party === "Republican"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-gray-100 text-gray-800"
                                                  }`}
                                              >
                                                {party}
                                              </span>
                                              <span className="font-bold ml-2">
                                                {count}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Nay Votes */}
                            <div
                              onClick={() => handleVoteBlockClick(vote.roll_call_id, 'nay')}
                              className={`relative group p-2 rounded cursor-pointer ${isDarkMode ? "bg-red-900/20" : "bg-red-50"} border ${isDarkMode ? "border-red-700/30" : "border-red-200"} hover:border-red-500 transition-all duration-300`}
                            >
                              <div className="text-center">
                                <div
                                  className={`text-lg font-bold ${isDarkMode ? "text-red-400" : "text-red-600"}`}
                                >
                                  {vote.nay}
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-red-300" : "text-red-700"} uppercase tracking-wide font-semibold`}
                                >
                                  Nay
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {vote.total > 0
                                    ? ((vote.nay / vote.total) * 100).toFixed(0)
                                    : 0}
                                  %
                                </div>
                              </div>

                              {/* Party Breakdown Tooltip for Nay */}
                              {(() => {
                                const matchingBreakdown =
                                  bill.vote_breakdowns && vote.roll_call_id
                                    ? bill.vote_breakdowns.find(
                                      (bd) =>
                                        bd.rollCallId === vote.roll_call_id,
                                    )
                                    : null;

                                if (!matchingBreakdown?.partyVoteCounts)
                                  return null;

                                const nayBreakdown = {
                                  Democrat:
                                    matchingBreakdown.partyVoteCounts.Democrat
                                      ?.Nay || 0,
                                  Republican:
                                    matchingBreakdown.partyVoteCounts.Republican
                                      ?.Nay || 0,
                                  Other:
                                    matchingBreakdown.partyVoteCounts.Other
                                      ?.Nay || 0,
                                };

                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div
                                      className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}
                                    >
                                      <div className="text-xs font-semibold mb-1 text-center">
                                        Nay Votes by Party
                                      </div>
                                      <div className="space-y-1">
                                        {Object.entries(nayBreakdown).map(
                                          ([party, count]) => (
                                            <div
                                              key={party}
                                              className="flex justify-between items-center text-xs"
                                            >
                                              <span
                                                className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${party === "Democrat"
                                                  ? "bg-blue-100 text-blue-800"
                                                  : party === "Republican"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-gray-100 text-gray-800"
                                                  }`}
                                              >
                                                {party}
                                              </span>
                                              <span className="font-bold ml-2">
                                                {count}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* No Vote */}
                            <div
                              onClick={() => handleVoteBlockClick(vote.roll_call_id, 'nv')}
                              className={`relative group p-2 rounded cursor-pointer ${isDarkMode ? "bg-yellow-900/20" : "bg-yellow-50"} border ${isDarkMode ? "border-yellow-700/30" : "border-yellow-200"} hover:border-yellow-500 transition-all duration-300`}
                            >
                              <div className="text-center">
                                <div
                                  className={`text-lg font-bold ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`}
                                >
                                  {vote.nv || 0}
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-yellow-300" : "text-yellow-700"} uppercase tracking-wide font-semibold`}
                                >
                                  No Vote
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {vote.total > 0
                                    ? (
                                      ((vote.nv || 0) / vote.total) *
                                      100
                                    ).toFixed(0)
                                    : 0}
                                  %
                                </div>
                              </div>

                              {/* Party Breakdown Tooltip for No Vote */}
                              {(() => {
                                const matchingBreakdown =
                                  bill.vote_breakdowns && vote.roll_call_id
                                    ? bill.vote_breakdowns.find(
                                      (bd) =>
                                        bd.rollCallId === vote.roll_call_id,
                                    )
                                    : null;

                                if (!matchingBreakdown?.partyVoteCounts)
                                  return null;

                                const nvBreakdown = {
                                  Democrat:
                                    matchingBreakdown.partyVoteCounts.Democrat
                                      ?.NV || 0,
                                  Republican:
                                    matchingBreakdown.partyVoteCounts.Republican
                                      ?.NV || 0,
                                  Other:
                                    matchingBreakdown.partyVoteCounts.Other
                                      ?.NV || 0,
                                };

                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div
                                      className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}
                                    >
                                      <div className="text-xs font-semibold mb-1 text-center">
                                        No Votes by Party
                                      </div>
                                      <div className="space-y-1">
                                        {Object.entries(nvBreakdown).map(
                                          ([party, count]) => (
                                            <div
                                              key={party}
                                              className="flex justify-between items-center text-xs"
                                            >
                                              <span
                                                className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${party === "Democrat"
                                                  ? "bg-blue-100 text-blue-800"
                                                  : party === "Republican"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-gray-100 text-gray-800"
                                                  }`}
                                              >
                                                {party}
                                              </span>
                                              <span className="font-bold ml-2">
                                                {count}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Absent */}
                            <div
                              onClick={() => handleVoteBlockClick(vote.roll_call_id, 'absent')}
                              className={`relative group p-2 rounded cursor-pointer ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"} border ${isDarkMode ? "border-gray-600/30" : "border-gray-200"} hover:border-gray-500 transition-all duration-300`}
                            >
                              <div className="text-center">
                                <div
                                  className={`text-lg font-bold ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {vote.absent || 0}
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"} uppercase tracking-wide font-semibold`}
                                >
                                  Absent
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {vote.total > 0
                                    ? (
                                      ((vote.absent || 0) / vote.total) *
                                      100
                                    ).toFixed(0)
                                    : 0}
                                  %
                                </div>
                              </div>

                              {/* Party Breakdown Tooltip for Absent */}
                              {(() => {
                                const matchingBreakdown =
                                  bill.vote_breakdowns && vote.roll_call_id
                                    ? bill.vote_breakdowns.find(
                                      (bd) =>
                                        bd.rollCallId === vote.roll_call_id,
                                    )
                                    : null;

                                if (!matchingBreakdown?.partyVoteCounts)
                                  return null;

                                const absentBreakdown = {
                                  Democrat:
                                    matchingBreakdown.partyVoteCounts.Democrat
                                      ?.Absent || 0,
                                  Republican:
                                    matchingBreakdown.partyVoteCounts.Republican
                                      ?.Absent || 0,
                                  Other:
                                    matchingBreakdown.partyVoteCounts.Other
                                      ?.Absent || 0,
                                };

                                return (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                    <div
                                      className={`px-3 py-2 rounded-lg shadow-lg border ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}
                                    >
                                      <div className="text-xs font-semibold mb-1 text-center">
                                        Absent by Party
                                      </div>
                                      <div className="space-y-1">
                                        {Object.entries(absentBreakdown).map(
                                          ([party, count]) => (
                                            <div
                                              key={party}
                                              className="flex justify-between items-center text-xs"
                                            >
                                              <span
                                                className={`inline-flex items-center justify-center w-24 px-2 py-0.5 rounded text-xs font-medium ${party === "Democrat"
                                                  ? "bg-blue-100 text-blue-800"
                                                  : party === "Republican"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-gray-100 text-gray-800"
                                                  }`}
                                              >
                                                {party}
                                              </span>
                                              <span className="font-bold ml-2">
                                                {count}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-800"></div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Compact Visual Vote Bar */}
                          <div className="mt-3">
                            <div className="relative">
                              <div
                                className={`w-full h-3 rounded-full overflow-hidden shadow-inner ${isDarkMode ? "bg-gray-800" : "bg-gray-200"} flex`}
                              >
                                <div
                                  className="bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 ease-in-out"
                                  style={{
                                    width:
                                      vote.total > 0
                                        ? `${(vote.yea / vote.total) * 100}%`
                                        : "0%",
                                  }}
                                  title={`Yea: ${vote.yea} votes (${vote.total > 0 ? ((vote.yea / vote.total) * 100).toFixed(1) : 0}%)`}
                                ></div>
                                <div
                                  className="bg-gradient-to-r from-red-500 to-red-600 transition-all duration-500 ease-in-out"
                                  style={{
                                    width:
                                      vote.total > 0
                                        ? `${(vote.nay / vote.total) * 100}%`
                                        : "0%",
                                  }}
                                  title={`Nay: ${vote.nay} votes (${vote.total > 0 ? ((vote.nay / vote.total) * 100).toFixed(1) : 0}%)`}
                                ></div>
                                {(vote.nv || 0) > 0 && (
                                  <div
                                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 transition-all duration-500 ease-in-out"
                                    style={{
                                      width:
                                        vote.total > 0
                                          ? `${((vote.nv || 0) / vote.total) * 100}%`
                                          : "0%",
                                    }}
                                    title={`No Vote: ${vote.nv || 0} votes (${vote.total > 0 ? (((vote.nv || 0) / vote.total) * 100).toFixed(1) : 0}%)`}
                                  ></div>
                                )}
                                {(vote.absent || 0) > 0 && (
                                  <div
                                    className={`bg-gradient-to-r ${isDarkMode ? "from-gray-600 to-gray-700" : "from-gray-400 to-gray-500"} transition-all duration-500 ease-in-out`}
                                    style={{
                                      width:
                                        vote.total > 0
                                          ? `${((vote.absent || 0) / vote.total) * 100}%`
                                          : "0%",
                                    }}
                                    title={`Absent: ${vote.absent || 0} votes (${vote.total > 0 ? (((vote.absent || 0) / vote.total) * 100).toFixed(1) : 0}%)`}
                                  ></div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Compact Footer */}
                          <div
                            className={`mt-3 pt-2 border-t ${isDarkMode ? "border-gray-600/50" : "border-gray-200/50"}`}
                          >
                            <div className="flex items-center justify-between text-xs">
                              <span
                                className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Total: {vote.total} votes
                              </span>
                              {vote.threshold && (
                                <span
                                  className={`${isDarkMode ? "text-blue-400" : "text-blue-600"} font-medium`}
                                >
                                  Required: {vote.threshold}
                                </span>
                              )}
                            </div>

                            {/* Detailed Votes Component */}
                            <VoteDetails
                              vote={vote}
                              billId={bill.id || billId}
                              isDarkMode={isDarkMode}
                              voteDetailsCache={voteDetailsCache}
                              setVoteDetailsCache={setVoteDetailsCache}
                              initialExpanded={expandedVoteFilter.get(vote.roll_call_id) !== undefined}
                              initialFilter={expandedVoteFilter.get(vote.roll_call_id) || 'all'}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Sponsors Card - now full width */}
              <section
                className={`rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-white"} p-5 border ${isDarkMode ? "border-gray-600" : "border-gray-200"} shadow-sm`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3
                      className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <InfoTooltip
                        content="A sponsor or co-sponsor is a legislator who introduces or supports a bill and takes responsibility for its passage through the legislative process"
                        isDarkMode={isDarkMode}
                        size="md"
                      >
                        Sponsors
                      </InfoTooltip>
                    </h3>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                  >
                    {bill.sponsors?.length || 0}
                  </span>
                </div>

                <SponsorSection bill={bill} isDarkMode={isDarkMode} />
              </section>

              {/* Official Bill Text Documents */}
              {bill.texts && bill.texts.length > 0 && (
                <div
                  className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"} mb-6`}
                >
                  <h4
                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-3 uppercase tracking-wide`}
                  >
                    Official Bill Documents ({bill.texts.length})
                  </h4>
                  <div className="space-y-2">
                    {bill.texts
                      .filter(
                        (text) => text.url && text.url.includes("legis.ga.gov"),
                      )
                      .sort((a, b) => (b.version || 0) - (a.version || 0))
                      .map((text, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode
                            ? "bg-gray-800/50 hover:bg-gray-800"
                            : "bg-white hover:bg-gray-100"
                            } transition-colors border ${isDarkMode ? "border-gray-600/30" : "border-gray-200"}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                              >
                                {text.description ||
                                  `Version ${text.version || idx + 1}`}
                              </span>
                              {text.version && (
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${isDarkMode
                                    ? "bg-gray-700 text-gray-300"
                                    : "bg-gray-200 text-gray-700"
                                    }`}
                                >
                                  v{text.version}
                                </span>
                              )}
                            </div>
                            <div
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Official Georgia Legislative PDF
                            </div>
                          </div>
                          <a
                            href={text.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`ml-4 px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1.5 ${isDarkMode
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                              } transition-colors`}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            View PDF
                          </a>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div
                className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6 flex flex-wrap gap-3`}
              >
                {/* View Official Bill - links to GGA legislation page */}
                {(() => {
                  // Get legislation_id from the texts array to construct official GGA page URL
                  const legislationId = bill.texts?.[0]?.legislation_id;
                  const officialPageUrl = legislationId
                    ? `https://www.legis.ga.gov/legislation/${legislationId}`
                    : null;

                  if (!officialPageUrl) return null;

                  return (
                    <a
                      href={officialPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                    >
                      <Scale className="w-5 h-5 mr-2" />
                      View Official Bill
                    </a>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Flag Inaccuracy Modal */}
      {bill && (
        <FlagInaccuracy
          isDarkMode={isDarkMode}
          billId={bill.id || bill._id}
          billNumber={bill.bill_number || billNumber}
          billUrl={window.location.href}
          isOpen={showFlagInaccuracy}
          onClose={() => setShowFlagInaccuracy(false)}
        />
      )}
    </>
  );
};

// End of BillDetail.jsx
export default BillDetail;
